import * as THREE from "three";
import { bus } from "../core/bus";

const NEUTRALIZED_DURATION = 1.6;

export class ThreatHud {
  private readonly marker: HTMLElement;
  private readonly leadPip: HTMLElement;
  private readonly neutralEl: HTMLElement;
  private neutralLife = 0;
  private pulse = 0;
  private readonly onEnemyKilled = ({ wasTopThreat }: { wasTopThreat: boolean }): void => {
    if (wasTopThreat) this.neutralLife = NEUTRALIZED_DURATION;
  };

  constructor() {
    this.marker = document.createElement("div");
    this.marker.style.cssText = [
      "position:fixed",
      "width:28px",
      "height:28px",
      "border:2px solid rgba(255,165,0,0)",
      "box-shadow:0 0 6px rgba(255,165,0,0)",
      "pointer-events:none",
      "transform:translate(-50%,-50%)",
      "display:none",
      "z-index:200",
    ].join(";");
    document.body.appendChild(this.marker);

    // Lead pip: where to aim so the shell and the moving top threat arrive
    // together. Rendered as a small rotated square (diamond).
    this.leadPip = document.createElement("div");
    this.leadPip.style.cssText = [
      "position:fixed",
      "width:10px",
      "height:10px",
      "border:2px solid rgba(255,220,90,0.9)",
      "pointer-events:none",
      "transform:translate(-50%,-50%) rotate(45deg)",
      "display:none",
      "z-index:200",
    ].join(";");
    document.body.appendChild(this.leadPip);

    this.neutralEl = document.createElement("div");
    this.neutralEl.style.cssText = [
      "position:fixed",
      "top:42%",
      "left:50%",
      "transform:translateX(-50%)",
      "font-family:monospace",
      "font-size:13px",
      "letter-spacing:0.2em",
      "color:rgba(255,165,0,0)",
      "pointer-events:none",
      "white-space:nowrap",
      "z-index:200",
    ].join(";");
    this.neutralEl.textContent = "THREAT NEUTRALIZED";
    document.body.appendChild(this.neutralEl);

    bus.on("enemy:killed", this.onEnemyKilled);
  }

  update(
    dt: number,
    camera: THREE.PerspectiveCamera,
    threatWorldPos: THREE.Vector3 | null,
    leadWorldPos: THREE.Vector3 | null = null,
  ): void {
    this.pulse += dt * 3.5;
    const pAlpha = 0.55 + 0.45 * Math.sin(this.pulse);

    if (leadWorldPos) {
      const ndc = leadWorldPos.clone().project(camera);
      if (ndc.z < 1 && Math.abs(ndc.x) < 1.2 && Math.abs(ndc.y) < 1.2) {
        const sx = ((ndc.x + 1) / 2) * window.innerWidth;
        const sy = ((-ndc.y + 1) / 2) * window.innerHeight;
        this.leadPip.style.left = `${sx.toFixed(0)}px`;
        this.leadPip.style.top = `${sy.toFixed(0)}px`;
        this.leadPip.style.display = "block";
      } else {
        this.leadPip.style.display = "none";
      }
    } else {
      this.leadPip.style.display = "none";
    }

    if (threatWorldPos) {
      const ndc = threatWorldPos.clone().project(camera);
      // Only show if in front of camera and within reasonable NDC range
      if (ndc.z < 1 && Math.abs(ndc.x) < 1.5 && Math.abs(ndc.y) < 1.5) {
        const sx = ((ndc.x + 1) / 2) * window.innerWidth;
        const sy = ((-ndc.y + 1) / 2) * window.innerHeight;
        this.marker.style.left = `${sx.toFixed(0)}px`;
        this.marker.style.top  = `${sy.toFixed(0)}px`;
        this.marker.style.display = "block";
        this.marker.style.borderColor = `rgba(255,165,0,${(pAlpha * 0.85).toFixed(2)})`;
        this.marker.style.boxShadow = `0 0 8px rgba(255,165,0,${(pAlpha * 0.4).toFixed(2)})`;
      } else {
        this.marker.style.display = "none";
      }
    } else {
      this.marker.style.display = "none";
    }

    if (this.neutralLife > 0) {
      this.neutralLife -= dt;
      const t = Math.max(0, this.neutralLife / NEUTRALIZED_DURATION);
      this.neutralEl.style.color = `rgba(255,165,0,${t.toFixed(2)})`;
    } else {
      this.neutralEl.style.color = "rgba(255,165,0,0)";
    }
  }

  dispose(): void {
    bus.off("enemy:killed", this.onEnemyKilled);
    this.marker.remove();
    this.leadPip.remove();
    this.neutralEl.remove();
  }
}
